<template>
  <div>
    <div class="app-container app-container--with-progress">
      <div class="app-container-route">
        <div class="page-intro">
          <h2>Find your member information</h2>
          <p>
            Please provide the following information so we can find your SoR
            membership.
          </p>
        </div>
        <div>
          <ValidationObserver ref="form">
            <form-component :page="page" />
          </ValidationObserver>
          <section class="form-controls">
            <button
              @click="findMembership"
              class="btn btn--positive"
              role="button"
            >
              Find my membership
            </button>
          </section>
        </div>
      </div>
      <div class="app-container-progress">
        <progress-bar />
      </div>
    </div>
    <spinner />
    <notification />
  </div>
</template>

<script>
import { ValidationObserver } from "vee-validate";
import { ref, onMounted } from "@vue/composition-api";
import Notification from "@/components/Notification";
import ProgressBar from "@/components/ProgressBar.vue";
import FormComponent from "@/components/FormComponent";
import { useShowSpinner } from "@/helpers/showSpinner";
import { CreateAccountDefinition } from "@/definitions/section-definitions";
import Spinner from "@/components/Spinner";
import { useNotification } from "@/helpers/notification";
import { useMemberDetails } from "@/helpers/member-details";
import { useMembershipTypes } from "@/helpers/membership";
import { doPost } from "@/helpers/fetch-helper";

export default {
  components: {
    ProgressBar,
    FormComponent,
    ValidationObserver,
    Spinner,
    Notification,
  },
  setup(_, ctx) {
    const page = CreateAccountDefinition.pages[0];
    const { setShowSpinner } = useShowSpinner();
    const form = ref(null);
    const { showNotification, ...notification } = useNotification();

    onMounted(() => {
      const params = parseQueryString();

      if (Object.keys(params).length === 0) return;

      page.fields = page.fields.map((field) => {
        let _ = params.find((p) => p.key === field.key);
        field.value = _.value;
        return field;
      });

      setTimeout(async () => {
        await findMembership();
      }, 500);
    });

    async function findMembership() {
      const success = await form.value.validate();

      if (!success) return;

      setShowSpinner(true);
      const data = page.fields.reduce((acc, cur) => {
        acc[cur.key] = cur.value;
        return acc;
      }, {});
      const response = await doPost("/findmembership", {
        ...data,
      });

      setShowSpinner(false);
      if (response.success) {
        if (response.data.exists) {
          notification.notificationShow.value = true;
          showNotification(
            "error",
            `This membership appears to be in use. Please <a href="/auth/requestsignin?returnurl=${window.location.pathname}">log in</a> to access your account`,
            true,
          );
          return;
        }
        const details = useMemberDetails();
        details.membershipNumber.value = data.membershipNumber;
        details.lastName.value = data.lastName;
        details.dateOfBirth.value = data.dateOfBirth;
        details.existing.value = response.success;
        details.lapsed.value = response.data.isExpired;

        useMembershipTypes().setMembershipData(
          response.data.type,
          response.data.status,
        );
        showNotification("success", "Successfully located your membership");

        if (response.data.linked)
          ctx.root.$router.push("/register/membership/review");
        else ctx.root.$router.push("/register/membership/contact-details");
        return;
      }

      showNotification("error", "We were unable to find your membership");
    }

    function parseQueryString() {
      const queryString = window.location.search;

      if (queryString === "") return {};

      let query = [];
      const kvPairs = (queryString[0] === "?"
        ? queryString.substring(1)
        : queryString
      ).split("&");

      kvPairs.forEach((kv) => {
        let _ = kv.split("=");
        query.push({
          key: decodeURIComponent(_[0]),
          value: decodeURIComponent(_[1] || ""),
        });
      });

      return query;
    }

    return {
      page,
      setShowSpinner,
      form,
      findMembership,
      ...notification,
    };
  },
};
</script>

<style lang="scss" scoped>
.btn--positive {
  min-width: 175px;
}
</style>
